import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { FiMenu, FiSave, FiArrowLeft } from 'react-icons/fi';
import { BsYoutube } from 'react-icons/bs';
import AdminSidebar from './AdminSidebar';
import { getSermon, createSermon, updateSermon } from '../../Redux/slices/sermonSlice';

const AddEditSermon = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isEditMode = Boolean(id);
  const { loading } = useSelector(state => state.sermons);
  const { user } = useSelector(state => state.auth);

  const [formData, setFormData] = useState({
    title: '',
    preacher: '',
    date: '',
    category: '',
    sermonType: 'Regular',
    youtubeUrl: '',
    description: '',
    scripture: '',
    tags: '',
    isFeatured: false
  });
  const [thumbnailFile, setThumbnailFile] = useState(null);
  const [thumbnailPreview, setThumbnailPreview] = useState(null);

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isEditMode && id) {
      dispatch(getSermon(id)).then((result) => {
        if (result.payload?.data?.sermon) {
          const sermon = result.payload.data.sermon;
          setFormData({
            title: sermon.title || '',
            preacher: sermon.preacher || '',
            date: sermon.date ? sermon.date.split('T')[0] : '',
            category: sermon.category || '',
            sermonType: sermon.sermonType || 'Regular',
            youtubeUrl: sermon.youtubeUrl || '',
            description: sermon.description || '',
            scripture: sermon.scripture || '',
            tags: sermon.tags ? sermon.tags.join(', ') : '',
            isFeatured: sermon.isFeatured || false
          });
        }
      });
    }
  }, [isEditMode, id, dispatch]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
    // Clear error for this field
    if (errors[name]) {
      setErrors({
        ...errors,
        [name]: ''
      });
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setThumbnailFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setThumbnailPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const validateYoutubeUrl = (url) => {
    const youtubeRegex = /^(https?:\/\/)?(www\.)?(youtube\.com|youtu\.be)\/.+$/;
    return youtubeRegex.test(url);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }

    if (!formData.preacher.trim()) {
      newErrors.preacher = 'Preacher name is required';
    }

    if (!formData.date) {
      newErrors.date = 'Date is required';
    }

    if (!formData.category.trim()) {
      newErrors.category = 'Category is required';
    }

    if (!formData.youtubeUrl.trim()) {
      newErrors.youtubeUrl = 'YouTube URL is required';
    } else if (!validateYoutubeUrl(formData.youtubeUrl)) {
      newErrors.youtubeUrl = 'Please enter a valid YouTube URL';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (validateForm()) {
      const formDataToSend = new FormData();
      
      // Append all form fields
      Object.keys(formData).forEach(key => {
        if (key === 'tags') {
          const tagsArray = formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag);
          tagsArray.forEach(tag => formDataToSend.append('tags[]', tag));
        } else {
          formDataToSend.append(key, formData[key]);
        }
      });
      
      // Append thumbnail if selected
      if (thumbnailFile) {
        formDataToSend.append('thumbnail', thumbnailFile);
      }

      try {
        if (isEditMode) {
          await dispatch(updateSermon({ id, sermonData: formDataToSend })).unwrap();
          alert('Sermon updated successfully!');
        } else {
          await dispatch(createSermon(formDataToSend)).unwrap();
          alert('Sermon added successfully!');
        }
        navigate('/admin/sermons');
      } catch (error) {
        alert(error || 'Failed to save sermon');
      }
    }
  };


  return (
    <div className="min-h-screen bg-gray-100">
      <AdminSidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} currentPage="sermons" />

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Top Bar */}
        <header className="bg-white shadow-sm">
          <div className="flex items-center justify-between p-4">
            <button onClick={() => setSidebarOpen(true)} className="lg:hidden">
              <FiMenu size={24} />
            </button>
            <h2 className="text-2xl font-bold text-gray-800">
              {isEditMode ? 'Edit Sermon' : 'Add New Sermon'}
            </h2>
            <div className="flex items-center space-x-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-800">
                  {user ? `${user.firstName} ${user.lastName}` : 'Admin User'}
                </p>
                <p className="text-xs text-gray-500">{user?.email || 'admin@houseofpraise.com'}</p>
              </div>
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center text-white font-semibold">
                {user ? user.firstName.charAt(0).toUpperCase() : 'A'}
              </div>
            </div>
          </div>
        </header>

        {/* Content */}
        <main className="p-6">
          {/* Back Button */}
          <Link
            to="/admin/sermons"
            className="inline-flex items-center space-x-2 text-blue-600 hover:text-blue-700 font-medium mb-6"
          >
            <FiArrowLeft size={20} />
            <span>Back to Sermons</span>
          </Link>

          {/* Form */}
          <div className="bg-white rounded-xl shadow-md p-6 md:p-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Title */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Sermon Title <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="title"
                  value={formData.title}
                  onChange={handleChange}
                  className={`block w-full px-4 py-3 border ${errors.title ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  placeholder="Enter sermon title"
                />
                {errors.title && <p className="text-red-500 text-sm mt-1">{errors.title}</p>}
              </div>

              {/* Preacher and Date */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Preacher Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="preacher"
                    value={formData.preacher}
                    onChange={handleChange}
                    className={`block w-full px-4 py-3 border ${errors.preacher ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    placeholder="Enter preacher name"
                  />
                  {errors.preacher && <p className="text-red-500 text-sm mt-1">{errors.preacher}</p>}
                </div>

                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    name="date"
                    value={formData.date}
                    onChange={handleChange}
                    className={`block w-full px-4 py-3 border ${errors.date ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  />
                  {errors.date && <p className="text-red-500 text-sm mt-1">{errors.date}</p>}
                </div>
              </div>

              {/* Category and Sermon Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Category */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Category <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className={`block w-full px-4 py-3 border ${errors.category ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  >
                    <option value="">Select category</option>
                    <option value="Faith">Faith</option>
                    <option value="Love">Love</option>
                    <option value="Healing">Healing</option>
                    <option value="Purpose">Purpose</option>
                    <option value="Prayer">Prayer</option>
                    <option value="Worship">Worship</option>
                    <option value="Family">Family</option>
                    <option value="Finance">Finance</option>
                    <option value="Prophecy">Prophecy</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.category && <p className="text-red-500 text-sm mt-1">{errors.category}</p>}
                </div>

                {/* Sermon Type */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Sermon Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    name="sermonType"
                    value={formData.sermonType}
                    onChange={handleChange}
                    className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="Regular">Regular Sermon</option>
                    <option value="Series">Sermon Series</option>
                    <option value="Special Event">Special Event</option>
                    <option value="Guest Speaker">Guest Speaker</option>
                  </select>
                </div>
              </div>

              {/* Scripture Reference */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Scripture Reference
                </label>
                <input
                  type="text"
                  name="scripture"
                  value={formData.scripture}
                  onChange={handleChange}
                  className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., John 3:16"
                />
              </div>

              {/* YouTube URL */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  YouTube URL <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <BsYoutube className="text-red-600" size={20} />
                  </div>
                  <input
                    type="url"
                    name="youtubeUrl"
                    value={formData.youtubeUrl}
                    onChange={handleChange}
                    className={`block w-full pl-12 pr-4 py-3 border ${errors.youtubeUrl ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                    placeholder="https://youtube.com/watch?v=..."
                  />
                </div>
                {errors.youtubeUrl && <p className="text-red-500 text-sm mt-1">{errors.youtubeUrl}</p>}
                <p className="text-gray-500 text-sm mt-1">
                  When users click to watch, they'll be redirected to this YouTube link
                </p>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Description <span className="text-red-500">*</span>
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  rows="5"
                  className={`block w-full px-4 py-3 border ${errors.description ? 'border-red-500' : 'border-gray-300'} rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent`}
                  placeholder="Enter sermon description"
                />
                {errors.description && <p className="text-red-500 text-sm mt-1">{errors.description}</p>}
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Tags
                </label>
                <input
                  type="text"
                  name="tags"
                  value={formData.tags}
                  onChange={handleChange}
                  className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="e.g., faith, healing, miracles (comma separated)"
                />
                <p className="text-gray-500 text-sm mt-1">
                  Separate tags with commas
                </p>
              </div>

              {/* Thumbnail Image */}
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Sermon Thumbnail
                </label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="block w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                {thumbnailPreview && (
                  <div className="mt-4">
                    <img 
                      src={thumbnailPreview} 
                      alt="Thumbnail preview" 
                      className="w-48 h-32 object-cover rounded-lg border border-gray-300"
                    />
                  </div>
                )}
                <p className="text-gray-500 text-sm mt-1">
                  Upload an image for the sermon thumbnail (JPG, PNG, GIF, WebP - Max 5MB)
                </p>
              </div>

              {/* Featured Checkbox */}
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="isFeatured"
                  checked={formData.isFeatured}
                  onChange={handleChange}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label className="ml-2 block text-sm text-gray-700">
                  Mark as Featured Sermon
                </label>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center space-x-4 pt-6 border-t border-gray-200">
                <button
                  type="submit"
                  disabled={loading}
                  className="flex-1 md:flex-none flex items-center justify-center space-x-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-8 rounded-lg transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <FiSave size={20} />
                  <span>{loading ? 'Saving...' : (isEditMode ? 'Update Sermon' : 'Save Sermon')}</span>
                </button>
                <Link
                  to="/admin/sermons"
                  className="flex-1 md:flex-none flex items-center justify-center space-x-2 bg-gray-200 hover:bg-gray-300 text-gray-800 font-semibold py-3 px-8 rounded-lg transition"
                >
                  <span>Cancel</span>
                </Link>
              </div>
            </form>
          </div>
        </main>
      </div>

      {/* Overlay for mobile sidebar */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AddEditSermon;
